<!--
column {
  label
  field
  isSortable
}
-->

<template>
  <div>
    <div :class="['t-table-new', { '-full-height': fullHeight }]">
      <div class="t-table-new-content">
        <slot>
          <table>
            <thead>
              <tr>
                <th
                  v-for="(column, key) in columnsVisible"
                  :key="key"
                  :data-column="setColumnSortable(column)"
                  @click="handleSortBy(column)"
                >
                  <div class="t-head-column">
                    {{ column.label }}
                    <Icon
                      v-if="column.isSortable"
                      class="t-ml-sm icon-sortable"
                      name="ArrowRight"
                      :class="sortableClass(column)"
                      :handle-click="() => handleSortBy(column)"
                    />
                  </div>
                </th>
                <th v-if="hasActionsSlot" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in data"
                :key="index"
                :class="{ 'hide-bottom': isLastRow(index) && hideBottom }"
                @click="handleRowClick(item)"
              >
                <slot
                  name="cells"
                  :item="item"
                >
                  <td
                    v-for="(value, key) in item"
                    :key="key"
                  >
                    {{ value }}
                  </td>
                </slot>
                <td
                  v-if="hasActionsSlot"
                  class="t-table-actions"
                >
                  <div class="t-actions">
                    <slot
                      name="actions"
                      :item="item"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </slot>
        <div
          v-if="!data"
          class="empty-data"
        >
          <Icon
            class="alert-item"
            name="Alert"
          />
          {{ noDataLabel }}
        </div>
      </div>
    </div>
    <Pagination
      v-if="!hiddenPagination"
      :rows-number="rowsNumber"
      :rows-per-page="rowsPerPage"
      :start-row="startRow"
      :dropdown-direction="dropdownDirection"
      :result-per-page-label="$t('general.table.records_per_page')"
      @onChangePage="handleChangePage"
      @onChangeRowsPerPage="handleChangeRowsPerPage"
    />
  </div>
</template>

<script>
import Icon from '../Icon/Icon.vue';
import Pagination from '../Pagination/PaginationLegacy.vue';

export default {
  name: 'TTable',
  components: {
    Icon,
    Pagination,
  },
  props: {
    columns: { type: Array, default: () => [] },
    visibleColumns: { type: Array, default: () => [] },
    data: { type: Array, default: () => [] },
    descending: { type: Boolean, default: false },
    // page: { type: Number, default: 1 },
    rowsNumber: { type: Number, default: 0 },
    rowsPerPage: { type: Number, default: 15 },
    sortBy: { type: String, default: 'created_at' },
    startRow: { type: Number, default: 0 },
    noDataLabel: { type: String, default: 'No data available' },
    hiddenPagination: { type: Boolean, default: false },
    dropdownDirection: { type: String, default: 'bottom' },
    fullHeight: { type: Boolean, default: true },
    hideBottom: { type: Boolean, default: false },
  },
  data() {
    return {
      orderBy: null,
    };
  },
  computed: {
    hasActionsSlot() {
      return !!this.$slots.actions || !!this.$scopedSlots.actions;
    },
    columnsVisible() {
      return this.visibleColumns.length > 0
        ? this.columns.filter((column) => this.visibleColumns.includes(column.name))
        : this.columns;
    },
  },
  watch: {
    descending(value) {
      this.orderBy = value ? 1 : -1;
    },
  },
  methods: {
    handleChangePage(value) {
      this.$emit('onChangeFilter', value);
    },
    handleChangeRowsPerPage(value) {
      this.$emit('onChangeFilter', { rowsPerPage: value });
    },
    handleSortBy({ field, isSortable }) {
      if (!isSortable) {
        return;
      }
      this.handleSorterOrder();
      this.$emit('onChangeFilter', {
        sortBy: field,
        descending: this.orderBy > 0,
      });
    },
    handleSorterOrder() {
      if (this.orderBy < 0) {
        this.orderBy = 1;
        return;
      }
      this.orderBy = -1;
    },
    setColumnSortable({ isSortable }) {
      return isSortable ? 'sortable' : '';
    },
    handleRowClick(item) {
      this.$emit('onClick', item);
    },
    sortableClass({ field }) {
      const isActiveField = this.sortBy === field;
      return {
        'icon-active': isActiveField && this.orderBy,
        'icon-down': isActiveField && this.orderBy > 0,
      };
    },
    isLastRow(index) {
      return index === this.data.length - 1;
    },
  },
};
</script>

<style lang="scss" scoped>
.t-table-new {
  background: #fff;
  color: #979797;
  font-size: 14px;
  overflow: auto;

  &.-full-height {
    height: calc(100vh - 320px);
  }

  table {
    border-collapse: collapse;
    width: 100%;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 1;

    tr {
      background: #faf9fd;
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      height: 10px;
    }

    th {
      color: rgb(36, 59, 83);
      font-weight: 500;
      text-align: left;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }
    td {
      color: rgb(72, 101, 129);
      height: 48px;
      padding: 7px 16px;
    }
  }

  tr:hover {
    background: #f3f3f3;
  }

  th,
  td {
    background-color: inherit;
    font-size: 12px;
    padding: 7px 16px;
  }

  th[data-column="sortable"]:hover {
    .icon-sortable {
      opacity: 1;
    }
  }

  .icon-sortable {
    opacity: 0;
    width: 15px;
    height: 15px;
    fill: var(--track-color-bluegray-90);
    transform: rotate(90deg);
  }

  .icon-down {
    transform: rotate(-90deg);
  }

  .icon-active {
    opacity: 1;
  }

  .t-table-actions {
    position: sticky;
    right: 0;
    z-index: 0;
  }

  tr:hover .t-actions {
    opacity: 1;
    background-color: #f3f3f3;
  }

  .t-actions {
    position: absolute;
    right: 0;
    margin-right: 10px;
    margin-top: -10px;
    background-color: transparent;
    display: flex;
    opacity: 0;
    transition: opacity 0.2s linear;
  }

  .t-head-column {
    display: flex;
    align-items: center;
  }

  .empty-data {
    display: flex;
    align-items: center;
    color: black;
    padding: var(--track-spacing-md);
    font-size: var(--track-font-size-xxs);

    .alert-item {
      margin-right: var(--track-spacing-sm);
    }
  }
  .hide-bottom {
    border-bottom: 0;
  }
}
</style>
